import React, { useState, useRef, useEffect } from 'react';
import { api } from '../../api';
import { getStationKey } from '../../lib/posStation';
import orderRefund from '../../lib/orderRefund';

const EFTPOS_POLL_MS = 1500;
const UNANSWERED_PROMPT_MS = 32_000;

const PAYMENT_LABEL = {
  cash: 'Cash',
  card_paid: 'Card',
  split: 'Cash and card',
  cash_refunded: 'Cash, fully refunded',
  card_refunded: 'Card, fully refunded',
  split_refunded: 'Split, fully refunded',
};

function lineHint(line) {
  const parts = [];
  if (line.refunded_quantity > 0) {
    parts.push(`${line.refunded_quantity} refunded`);
  }
  if (line.held_quantity > 0) {
    parts.push(`${line.held_quantity} refund in progress`);
  }
  if (line.refundable_quantity > 0 && (line.refunded_quantity > 0 || line.held_quantity > 0)) {
    parts.push(`${line.refundable_quantity} left`);
  }
  return parts.join(' · ');
}

function dollars(n) {
  return `$${orderRefund.money(n).toFixed(2)}`;
}

// Refund is tied to one order from today's service. Every remaining unit
// starts selected; staff untick what they are keeping. The amount is the
// sum of what's still ticked.
export default function PosRefundModal({ onDone, onCancel, certAutoReceipts = false, tyroReady = false }) {
  const [orderNumber, setOrderNumber] = useState('');
  const [sale, setSale] = useState(null);
  const [quantities, setQuantities] = useState({});
  // lookup | items | sending | waiting | prompt | done | declined
  const [phase, setPhase] = useState('lookup');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [printMsg, setPrintMsg] = useState('');
  const [payRequestId, setPayRequestId] = useState(null);
  const [quote, setQuote] = useState(null);
  const [pendingDecision, setPendingDecision] = useState(null);
  const [printing, setPrinting] = useState(false);
  const pollTimerRef = useRef(null);
  const answeredDecisionRef = useRef(null);
  const declineAutoPrintedRef = useRef(false);

  useEffect(() => () => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
  }, []);

  useEffect(() => {
    const decisionId = pendingDecision?.decision_id;
    if (!decisionId) return undefined;
    const t = setTimeout(() => {
      if (answeredDecisionRef.current === decisionId) return;
      answeredDecisionRef.current = decisionId;
      setPendingDecision(null);
      setPhase((p) => (p === 'prompt' ? 'waiting' : p));
    }, UNANSWERED_PROMPT_MS);
    return () => clearTimeout(t);
  }, [pendingDecision?.decision_id]);

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const selection = sale
    ? orderRefund.selectionFromQuantities(sale.lines, quantities)
    : { requested: [], total: 0 };
  const plan = sale && selection.total > 0
    ? orderRefund.planRefundTender({
      paymentMethod: orderRefund.basePaymentMethod(sale.payment_method),
      total: selection.total,
      cashRemaining: sale.cash_remaining,
      cardRemaining: sale.card_remaining,
    })
    : null;

  const cardBlocked = Boolean(
    plan && (plan.mode === 'card' || plan.mode === 'both') && !tyroReady
  );

  const autoPrintCertReceipts = async (id, { decline = false } = {}) => {
    if (!certAutoReceipts || !id) return;
    if (decline && declineAutoPrintedRef.current) return;
    if (decline) declineAutoPrintedRef.current = true;
    try {
      const { printTyroCertReceipts } = await import('../../lib/tyroReceiptPrint');
      setPrinting(true);
      setPrintMsg(decline ? 'Printing decline receipts…' : 'Printing refund receipts…');
      await printTyroCertReceipts(id);
      setPrintMsg(decline ? 'Decline receipts sent to printer.' : 'Refund receipts sent to printer.');
      if (!decline) {
        setStatus((prev) => `${prev} — refund receipts printed`);
      }
    } catch (err) {
      console.warn('[pos] refund cert auto-print:', err.message);
      setPrintMsg(err.message || 'Auto-print failed — use Print card receipt');
    } finally {
      setPrinting(false);
    }
  };

  const watchTerminal = (id) => {
    stopPolling();
    pollTimerRef.current = setInterval(async () => {
      try {
        const st = await api.getEftposStatus(id);
        if (!st.pending_decision?.decision_id) {
          setPendingDecision(null);
        }
        if (st.approved) {
          const approvedText = st.result_text || st.progress_text || 'Refund approved';
          setStatus(approvedText);
          const decisionId = st.pending_decision?.decision_id;
          if (decisionId && answeredDecisionRef.current !== decisionId) {
            answeredDecisionRef.current = decisionId;
            void api.answerEftposPrompt(id, decisionId, 'NO').catch(() => {});
          }
          stopPolling();
          setPendingDecision(null);
          setPhase('done');
          void autoPrintCertReceipts(id, { decline: false });
        } else if (st.failed) {
          stopPolling();
          setPendingDecision(null);
          setPhase('declined');
          const text = st.result_text || st.progress_text || 'Refund declined';
          setError(text);
          setStatus(text);
          void autoPrintCertReceipts(id, { decline: true });
        } else if (st.pending_decision?.decision_id
          && answeredDecisionRef.current !== st.pending_decision.decision_id) {
          setPendingDecision(st.pending_decision);
          setPhase('prompt');
          setStatus(st.result_text || st.progress_text || st.pending_decision.question || 'Terminal needs an answer');
        } else if (st.progress_text) {
          setStatus(st.progress_text);
        } else if (st.result_text) {
          setStatus(st.result_text);
        } else if (st.tyro_status) {
          setStatus(st.tyro_status);
        }
      } catch (err) {
        console.warn('[pos] refund poll error:', err.message);
      }
    }, EFTPOS_POLL_MS);
  };

  const lookupOrder = async (event) => {
    event?.preventDefault();
    const number = orderNumber.trim();
    if (!number || phase === 'sending') return;
    setError('');
    setSale(null);
    try {
      const found = await api.lookupPosRefund(number);
      setSale(found);
      setQuantities(orderRefund.initialQuantities(found.lines));
      setPhase('items');
    } catch (err) {
      setPhase('lookup');
      setError(err.message || 'Could not look up that order');
    }
  };

  const setLineQty = (line, next) => {
    const qty = Math.max(0, Math.min(line.refundable_quantity, next));
    setQuantities((prev) => ({ ...prev, [line.id]: qty }));
    setError('');
  };

  const selectAll = () => {
    if (!sale) return;
    setQuantities(orderRefund.initialQuantities(sale.lines));
    setError('');
  };

  const startRefund = async (prefer) => {
    if (!sale || phase === 'sending' || phase === 'waiting') return;
    if (!selection.requested.length) {
      setError('Select at least one item to refund');
      return;
    }
    setPhase('sending');
    setError('');
    setPrintMsg('');
    setQuote(null);
    setStatus(prefer === 'cash' ? 'Recording cash refund…' : 'Sending refund to the terminal…');
    answeredDecisionRef.current = null;
    declineAutoPrintedRef.current = false;
    try {
      const result = await api.startPosRefund({
        order_number: sale.order_number,
        lines: selection.requested,
        prefer,
      }, getStationKey());
      setQuote(result);
      if (result.settled) {
        const cash = orderRefund.money(result.cash_amount);
        setStatus(`Refunded ${dollars(cash)} cash. Those items are back in stock.`);
        setPhase('done');
        return;
      }
      const id = result?.pay_request_id;
      if (!id) throw new Error(result?.error || 'Refund did not start');
      setPayRequestId(id);
      setPhase('waiting');
      const card = orderRefund.money(result.card_amount);
      setStatus(`Sending ${dollars(card)} to the terminal…`);
      watchTerminal(id);
    } catch (err) {
      stopPolling();
      setPhase('items');
      setError(err.message || 'Failed to start refund');
    }
  };

  const handleAnswerPrompt = async (answer) => {
    if (!payRequestId || !pendingDecision?.decision_id) return;
    try {
      await api.answerEftposPrompt(payRequestId, pendingDecision.decision_id, answer);
      answeredDecisionRef.current = pendingDecision.decision_id;
      setPendingDecision(null);
      setPhase('waiting');
      setStatus('Continuing on terminal…');
    } catch (err) {
      setError(err.message || 'Failed to answer prompt');
    }
  };

  const handlePrintReceipt = async () => {
    if (!payRequestId || printing) return;
    setPrinting(true);
    setPrintMsg('');
    try {
      const { printTyroCardReceipt } = await import('../../lib/tyroReceiptPrint');
      await printTyroCardReceipt(payRequestId, {
        which: certAutoReceipts ? 'all' : 'customer',
      });
      setPrintMsg('Card receipt sent to printer.');
    } catch (err) {
      setPrintMsg(err.message || 'Print failed');
      if (phase !== 'declined') setError(err.message || 'Print failed');
    } finally {
      setPrinting(false);
    }
  };

  const handleCancel = async () => {
    if (phase === 'sending') return;
    if (phase === 'done' || phase === 'declined' || phase === 'lookup' || phase === 'items') {
      stopPolling();
      onCancel();
      return;
    }
    if (!payRequestId) {
      stopPolling();
      onCancel();
      return;
    }
    setStatus('Cancelling…');
    try {
      const result = await api.cancelEftpos(payRequestId);
      if (result?.approved) {
        setStatus(result.result_text || result.progress_text || 'Refund approved');
        stopPolling();
        setPhase('done');
      } else if (result?.failed) {
        stopPolling();
        setPhase('declined');
        const text = result.result_text || result.progress_text || 'Refund cancelled';
        setError(text);
        setStatus(text);
      } else {
        setStatus(result?.progress_text || result?.result_text || 'Cancelling…');
      }
    } catch (err) {
      setStatus(err.message || 'Cancelling…');
    }
  };

  const busy = phase === 'sending' || phase === 'waiting' || phase === 'prompt';
  const displayAmount = quote
    ? (quote.card_amount > 0 ? quote.card_amount : quote.amount)
    : selection.total;
  const cashHandover = phase === 'done' && quote && orderRefund.money(quote.cash_amount) > 0 && quote.card_amount > 0
    ? `Also give ${dollars(quote.cash_amount)} cash.`
    : '';

  const renderActions = () => {
    if (!plan || plan.error) return null;
    if (plan.mode === 'choice') {
      return (
        <>
          <button
            type="button"
            className="pos-modal-btn pos-modal-btn-secondary"
            onClick={() => startRefund('cash')}
          >
            Refund {dollars(plan.cash)} cash
          </button>
          <button
            type="button"
            className="pos-modal-btn pos-modal-btn-primary"
            onClick={() => startRefund('card')}
            disabled={!tyroReady}
          >
            Send {dollars(plan.card)} to terminal
          </button>
        </>
      );
    }
    if (plan.mode === 'cash') {
      return (
        <button
          type="button"
          className="pos-modal-btn pos-modal-btn-primary"
          onClick={() => startRefund('cash')}
        >
          Refund {dollars(plan.cash)} cash
        </button>
      );
    }
    if (plan.mode === 'both') {
      return (
        <button
          type="button"
          className="pos-modal-btn pos-modal-btn-primary"
          onClick={() => startRefund('both')}
          disabled={!tyroReady}
        >
          Refund {dollars(plan.cash)} cash and {dollars(plan.card)} to card
        </button>
      );
    }
    return (
      <button
        type="button"
        className="pos-modal-btn pos-modal-btn-primary"
        onClick={() => startRefund('card')}
        disabled={!tyroReady}
      >
        Send {dollars(plan.card)} to terminal
      </button>
    );
  };

  return (
    <div className="pos-modal-backdrop" onClick={busy ? undefined : handleCancel}>
      <div className="pos-modal pos-refund-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pos-modal-header">
          <h2>Refund</h2>
          <button type="button" className="pos-modal-close" onClick={handleCancel} disabled={phase === 'sending'}>
            ×
          </button>
        </div>

        {phase === 'lookup' && (
          <form onSubmit={lookupOrder}>
            <div className="pos-settings-field">
              <label className="pos-settings-label" htmlFor="pos-refund-order">Order number</label>
              <input
                id="pos-refund-order"
                className="pos-settings-input"
                type="text"
                placeholder="B12"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                autoFocus
                autoCapitalize="characters"
              />
            </div>
            <p className="pos-refund-note">Today&apos;s sale only. Untick any items you are keeping.</p>
            {error && <div className="pos-settings-status pos-settings-status-err pos-terminal-live-error">{error}</div>}
            <div className="pos-modal-actions">
              <button type="button" className="pos-modal-btn pos-modal-btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
              <button
                type="submit"
                className="pos-modal-btn pos-modal-btn-primary"
                disabled={!orderNumber.trim()}
              >
                Find order
              </button>
            </div>
          </form>
        )}

        {phase === 'items' && sale && (
          <>
            <div className="pos-refund-sale">
              <strong>{sale.order_number}</strong>
              {sale.customer_name ? <span>{sale.customer_name}</span> : null}
              <span>{PAYMENT_LABEL[sale.payment_method] || sale.payment_method}</span>
            </div>
            {sale.payment_method === 'split' && (
              <p className="pos-refund-note">
                Cash left {dollars(sale.cash_remaining)} · Card left {dollars(sale.card_remaining)}
              </p>
            )}
            <div className="pos-refund-toolbar">
              <span>Items start selected. Untick anything you are keeping.</span>
              <button type="button" className="pos-settings-link" onClick={selectAll}>Select all</button>
            </div>
            {sale.lines.every((line) => line.refundable_quantity <= 0) && (
              <p className="pos-refund-note">Nothing left to refund on this order.</p>
            )}
            <div className="pos-refund-lines">
              {sale.lines.map((line) => {
                const qty = quantities[line.id] || 0;
                const locked = line.refundable_quantity <= 0;
                const amount = qty > 0
                  ? orderRefund.money(orderRefund.money(line.unit_price) * qty)
                  : 0;
                const hint = lineHint(line);
                return (
                  <div key={line.id} className={`pos-refund-line${qty > 0 ? '' : ' is-off'}`}>
                    <input
                      id={`pos-refund-line-${line.id}`}
                      type="checkbox"
                      checked={qty > 0}
                      disabled={locked}
                      onChange={(e) => setLineQty(line, e.target.checked ? line.refundable_quantity : 0)}
                    />
                    <label htmlFor={`pos-refund-line-${line.id}`} className="pos-refund-line-name">
                      <span>{line.name}</span>
                      {line.options_label ? <span className="pos-refund-line-meta">{line.options_label}</span> : null}
                      {line.item_note ? <span className="pos-refund-line-meta">{line.item_note}</span> : null}
                      {hint ? <span className="pos-refund-line-meta">{hint}</span> : null}
                      {locked && !hint ? <span className="pos-refund-line-meta">Refunded</span> : null}
                    </label>
                    <span className="pos-refund-line-price">{dollars(amount)}</span>
                    {!locked && line.refundable_quantity > 1 && qty > 0 && (
                      <div className="pos-refund-qty">
                        <button type="button" onClick={() => setLineQty(line, qty - 1)} disabled={qty <= 1} aria-label={`Fewer ${line.name}`}>−</button>
                        <span>{qty} of {line.refundable_quantity}</span>
                        <button type="button" onClick={() => setLineQty(line, qty + 1)} disabled={qty >= line.refundable_quantity} aria-label={`More ${line.name}`}>+</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="pos-refund-total">
              <span>Refund</span>
              <span>{dollars(selection.total)}</span>
            </div>
            {plan?.error && <div className="pos-settings-status pos-settings-status-err">{plan.error}</div>}
            {!plan && sale.lines.some((line) => line.refundable_quantity > 0) && (
              <div className="pos-refund-note">Select at least one item to refund.</div>
            )}
            {plan?.mode === 'choice' && !tyroReady && (
              <div className="pos-refund-note">Card refunds need the Tyro terminal. Cash is still available.</div>
            )}
            {cardBlocked && !plan?.error && (
              <div className="pos-settings-status pos-settings-status-err">
                This refund has to go to the card terminal, and Tyro is not set up on this POS.
              </div>
            )}
            {error && <div className="pos-settings-status pos-settings-status-err pos-terminal-live-error">{error}</div>}
            <div className="pos-modal-actions" style={{ flexWrap: 'wrap' }}>
              <button
                type="button"
                className="pos-modal-btn pos-modal-btn-secondary"
                onClick={() => { setPhase('lookup'); setError(''); }}
              >
                Back
              </button>
              {renderActions()}
            </div>
          </>
        )}

        {(phase === 'sending' || phase === 'waiting' || phase === 'prompt') && (
          <>
            <div className="pos-eftpos-body">
              <div className="pos-eftpos-amount">{dollars(displayAmount)}</div>
              <div className="pos-eftpos-status">{status}</div>
              {phase === 'prompt' && pendingDecision && (
                <div className="pos-eftpos-prompt">
                  <div className="pos-eftpos-prompt-q">{pendingDecision.question}</div>
                  <div className="pos-eftpos-prompt-actions">
                    {(pendingDecision.options?.length ? pendingDecision.options : ['YES', 'NO']).map((opt) => {
                      const label = typeof opt === 'string' ? opt : (opt?.label || opt?.value || String(opt));
                      const value = typeof opt === 'string' ? opt : (opt?.value || opt?.label || String(opt));
                      return (
                        <button
                          key={value}
                          type="button"
                          className="pos-modal-btn pos-modal-btn-secondary"
                          onClick={() => handleAnswerPrompt(value)}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="pos-modal-actions">
              <button type="button" className="pos-modal-btn pos-modal-btn-danger" onClick={handleCancel} disabled={phase === 'sending'}>
                Cancel refund
              </button>
            </div>
          </>
        )}

        {phase === 'done' && (
          <>
            <div className="pos-eftpos-body">
              <div className="pos-completion-check" aria-hidden>✓</div>
              <div className="pos-eftpos-amount">{dollars(quote?.amount || displayAmount)}</div>
              <div className="pos-eftpos-status">{status}</div>
              {cashHandover && <div className="pos-eftpos-status">{cashHandover} Those items are back in stock.</div>}
              {!cashHandover && quote?.card_amount > 0 && (
                <div className="pos-eftpos-status">Those items are back in stock.</div>
              )}
              {printMsg && <div className="pos-eftpos-status">{printMsg}</div>}
              {error && <div className="pos-settings-status pos-settings-status-err">{error}</div>}
            </div>
            <div className="pos-modal-actions" style={{ flexWrap: 'wrap' }}>
              {payRequestId && (
                <button
                  type="button"
                  className="pos-modal-btn pos-modal-btn-secondary"
                  onClick={handlePrintReceipt}
                  disabled={printing}
                >
                  {printing ? 'Printing…' : 'Print card receipt'}
                </button>
              )}
              <button type="button" className="pos-modal-btn pos-modal-btn-primary" onClick={onDone}>
                Done
              </button>
            </div>
          </>
        )}

        {phase === 'declined' && (
          <>
            <div className="pos-eftpos-body">
              <div className="pos-eftpos-amount">{dollars(displayAmount)}</div>
              <div className="pos-settings-status pos-settings-status-err pos-terminal-live-error">{error || status}</div>
              <div className="pos-eftpos-status">Nothing was refunded. Stock is unchanged.</div>
              {printMsg && <div className="pos-eftpos-status">{printMsg}</div>}
            </div>
            <div className="pos-modal-actions" style={{ flexWrap: 'wrap' }}>
              {payRequestId && (
                <button
                  type="button"
                  className="pos-modal-btn pos-modal-btn-secondary"
                  onClick={handlePrintReceipt}
                  disabled={printing}
                >
                  {printing ? 'Printing…' : 'Print card receipt'}
                </button>
              )}
              <button type="button" className="pos-modal-btn pos-modal-btn-primary" onClick={onDone}>
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
